import type { ReactNode } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import type { ConfigFlavor, ConfigImage, InitVolume, QuotaRequest } from '@/api/opennebula';

// ---- Domain types --------------------------------------------------------

export type OsTab = 'distributions' | 'custom' | 'marketplace';
export type AuthMode = 'sshkey' | 'password';
export type FlavorType = 'standard' | 'processor-optimized' | 'memory-optimized';

export interface Tag {
  key: string;
  value: string;
}

export interface CreateState {
  name: string;
  osTab: OsTab;
  image: string;
  marketplaceApp: string;
  appFields: Record<string, string>;
  flavorType: FlavorType;
  gpuEnabled: boolean;
  gpuModel: string;
  flavor: string;
  volumes: InitVolume[];
  networks: import('@/api/opennebula').ConfigNetwork[];
  secgroups: string[];
  auth: AuthMode;
  sshkey: string;
  password: string;
  tags: Tag[];
}

// ---- Constants -----------------------------------------------------------

export const VOLUME_TYPE_LABELS: Record<string, string> = {
  hiops: 'High IOPS SSD',
  standard: 'Standard SSD',
  nvme: 'NVMe SSD',
};
export const VOLUME_TYPES = ['hiops', 'standard', 'nvme'];
export const FLAVOR_TYPES: { value: FlavorType; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'processor-optimized', label: 'Processor Optimized' },
  { value: 'memory-optimized', label: 'Memory Optimized' },
];

// ---- Helpers -------------------------------------------------------------

export interface DistroGroup {
  distro: string;
  images: ConfigImage[];
}

export function groupByDistro(images: ConfigImage[]): DistroGroup[] {
  const m = new Map<string, ConfigImage[]>();
  images.forEach((img) => {
    const d = img.distro || 'Other';
    m.set(d, [...(m.get(d) ?? []), img]);
  });
  return [...m.entries()].map(([distro, imgs]) => ({ distro, images: imgs }));
}

export function overQuotaMsg(remaining: QuotaRequest): string {
  const parts: string[] = [];
  if (remaining.cores < 0) parts.push(`${-remaining.cores} vCPU`);
  if (remaining.memory < 0) parts.push(`${-remaining.memory} GiB memory`);
  if (remaining.volumes < 0) parts.push(`${-remaining.volumes} GiB volume`);
  if (remaining.instances < 0) parts.push(`${-remaining.instances} instance(s)`);
  return parts.join(', ');
}

// ---- Section card wrapper ------------------------------------------------

/**
 * One stacked section of the create form: a Card with a titled header, an
 * optional header action and footer note, wrapping the section's controls.
 */
export function SectionCard({
  title,
  description,
  actions,
  footer,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card flat>
      <CardHeader actions={actions}>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardBody>{children}</CardBody>
      {footer ? <CardFooter style={{ justifyContent: 'flex-start' }}>{footer}</CardFooter> : null}
    </Card>
  );
}

// ---- Shared option-card styles -------------------------------------------

export const pickCard = (sel: boolean): React.CSSProperties => ({
  display: 'grid',
  gap: 6,
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
  color: 'var(--vn-fg-default)',
  border: `1px solid ${sel ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
  outline: sel ? '1px solid var(--vn-accent-solid)' : 'none',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-4)',
});
export const distroCard = (sel: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  font: 'inherit',
  color: 'var(--vn-fg-default)',
  border: `1px solid ${sel ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
  outline: sel ? '1px solid var(--vn-accent-solid)' : 'none',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-3)',
});
export const flavorCard = (sel: boolean, disabled: boolean): React.CSSProperties => ({
  display: 'grid',
  gap: 2,
  textAlign: 'left',
  cursor: disabled ? 'not-allowed' : 'pointer',
  font: 'inherit',
  color: disabled ? 'var(--vn-fg-subtle)' : 'var(--vn-fg-default)',
  opacity: disabled ? 0.5 : 1,
  border: `1px solid ${sel ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
  outline: sel ? '1px solid var(--vn-accent-solid)' : 'none',
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-3)',
});

// ---- Flavor picker grid --------------------------------------------------

/** Flavor (size) picker grid — shared by instance + load-balancer create so
 *  both render identical sizing cards (vCPU / RAM / code). */
export function FlavorGrid({
  flavors,
  value,
  onPick,
  emptyLabel = 'No instance types in this category.',
}: {
  flavors: ConfigFlavor[];
  value: string;
  onPick: (name: string) => void;
  emptyLabel?: string;
}) {
  if (!flavors.length) return <Alert tone="info">{emptyLabel}</Alert>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--vn-space-2)' }}>
      {flavors.map((f) => {
        const sel = f.name === value;
        return (
          <button key={f.name} type="button" disabled={f.disabled} onClick={() => onPick(f.name)} style={flavorCard(sel, !!f.disabled)}>
            <div style={{ fontWeight: 600 }}>{f.cores} vCPU</div>
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{f.memory} GB RAM</div>
            <div style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>{f.name}</div>
          </button>
        );
      })}
    </div>
  );
}
